import { fromJS, List } from "immutable";
import Project from "~/records/record.project.jsx";
import ProjectMap from "~/records/record.project_map.jsx";
import * as actionTypes from "~/store/action_types";

export const project = (state = new Project(), action) => {
  switch (action.type) {
    case actionTypes.EDIT_PROJECT_FROM_EVENT_STREAM_SUCCESS:
    case actionTypes.PROJECT_COMPLETED_FROM_EVENT_STREAM_SUCCESS:
    case actionTypes.PROJECT_REOPENED_FROM_EVENT_STREAM_SUCCESS:
    case actionTypes.PROJECT_MOVE_TO_TRASH_FROM_EVENT_STREAM_SUCCESS:
    case actionTypes.PROJECT_RESTORED_FROM_TRASH_FROM_EVENT_STREAM_SUCCESS:
    case actionTypes.PROJECT_MEMBERSHIP_GRANTED_FROM_EVENT_STREAM:
    case actionTypes.PROJECT_MEMBERSHIP_REVOKED_FROM_EVENT_STREAM: {
      const { response = {} } = action;
      const { entities = {}, result: id } = response;
      const { projects = {} } = entities;

      if (projects[id]) {
        return new Project(
          fromJS({
            ...state.toObject(),
            ...projects[id],
          })
        );
      }

      return state;
    }
    case actionTypes.ADD_MEMBERS_TO_PROJECT_SUCCESS:
      return new Project({
        ...state.toObject(),
        members: new List(action.response),
      });
    default:
      return state;
  }
};

const mergeEntities = (state, newProjects) => {
  return state.merge(newProjects.map((p) => new Project(p)));
};

const mergeWithPartialData = (state, ids, projects) => {
  return state.merge(
    ids.reduce((acc, id) => {
      const existingProject = state.get(`${id}`);
      const projectData = existingProject ? { ...existingProject.toObject(), ...projects[id] } : projects[id];
      return { ...acc, [id]: new Project(projectData) };
    }, {})
  );
};

const projects = (state = new ProjectMap(), action) => {
  switch (action.type) {
    case actionTypes.ADD_PROJECT_SUCCESS:
    case actionTypes.DUPLICATE_PROJECT_SUCCESS:
    case actionTypes.ADD_PROJECT_FROM_EVENT_STREAM_SUCCESS:
      return mergeEntities(state.remove(action.projectId), fromJS(action.response.entities.projects));
    case actionTypes.OPEN_PROJECT_SUCCESS:
    case actionTypes.COMPLETE_PROJECT_SUCCESS:
    case actionTypes.MOVE_PROJECT_TO_TRASH_SUCCESS:
    case actionTypes.EDIT_PROJECT_SUCCESS: {
      const { response = {} } = action;
      const { entities = {}, result = {} } = response;
      const { projects = {} } = entities;
      const { single: id } = result;
      return state.set(`${id}`, new Project(fromJS(projects[id])));
    }
    case actionTypes.REMOVE_SUBSCRIBER_SUCCESS:
    case actionTypes.ADD_SUBSCRIBER_SUCCESS: {
      const { parentType = "", parentId = "", response = [] } = action;
      if (parentType === "project") {
        return state.setIn([`${parentId}`, "subscribers"], new List(response));
      }

      return state;
    }
    case actionTypes.ADD_PROJECT_FROM_DATASET_SUCCESS: {
      const { response = {} } = action;
      const { entities = {}, result = {} } = response;
      const { projects = {} } = entities;
      const { project: id } = result;
      return state.set(`${id}`, new Project(fromJS(projects[id])));
    }
    case actionTypes.EDIT_PROJECT_FROM_EVENT_STREAM_SUCCESS:
    case actionTypes.PROJECT_COMPLETED_FROM_EVENT_STREAM_SUCCESS:
    case actionTypes.PROJECT_REOPENED_FROM_EVENT_STREAM_SUCCESS:
    case actionTypes.PROJECT_MOVE_TO_TRASH_FROM_EVENT_STREAM_SUCCESS:
    case actionTypes.PROJECT_RESTORED_FROM_TRASH_FROM_EVENT_STREAM_SUCCESS:
    case actionTypes.PROJECT_MEMBERSHIP_GRANTED_FROM_EVENT_STREAM: {
      const { response = {} } = action;
      const { result: id } = response;

      return state.set(`${id}`, project(state.get(`${id}`), action));
    }
    case actionTypes.PROJECT_MEMBERSHIP_REVOKED_FROM_EVENT_STREAM: {
      const { response = {}, loggedUserId = 0, isLoggedUserOwner = false } = action;
      const { entities = {}, result: id } = response;
      const { projects = {} } = entities;

      // logged user is removed from project
      if (!isLoggedUserOwner && loggedUserId && !projects[id]?.members?.includes(loggedUserId)) {
        return state.delete(`${id}`);
      }

      return state.set(`${id}`, project(state.get(`${id}`), action));
    }
    case actionTypes.TRASH_PROJECT:
      return state.filter((p) => {
        return action.response.id !== p.get(`id`);
      });
    case actionTypes.PERMANENTLY_DELETE_SUCCESS: {
      const { id = 0, objectType } = action;

      if (id > 0 && objectType === "Project") {
        return state.delete(`${id}`);
      }

      return state;
    }

    case actionTypes.LOAD_PROJECTS:
      return fromJS(action.response);
    case actionTypes.FETCH_PROJECTS_SUCCESS: {
      if (action?.response?.entities?.projects) {
        return mergeEntities(state, fromJS(action.response.entities.projects));
      }
      return state;
    }
    case actionTypes.FETCH_COMPANY_PROJECTS_SUCCESS:
    case actionTypes.FETCH_PROJECT_SUCCESS:
    case actionTypes.FETCH_ACTIVITY_LOGS_SUCCESS:
    case actionTypes.FETCH_WORKLOAD_PROJECTS_SUCCESS:
    case actionTypes.ANGULAR_INITIALIZE_TASKS:
    case actionTypes.ANGULAR_INIT_PROJECT_MODAL:
    case actionTypes.FETCH_TIME_RECORDS_SUCCESS:
    case actionTypes.FETCH_EXPENSES_SUCCESS:
    case actionTypes.FETCH_PROJECT_LIST_TIME_RECORDS_SUCCESS:
    case actionTypes.FETCH_PROJECT_EXPENSES_SUCCESS:
    case actionTypes.FETCH_PROJECT_TIME_RECORDS_BY_DATES_SUCCESS:
    case actionTypes.FETCH_TIMESHEET_TIME_RECORDS_SUCCESS:
      if (!action.response || !action.response.entities || !action.response.entities.projects) {
        return state;
      }
      return mergeEntities(state, fromJS(action.response.entities.projects));
    case actionTypes.FETCH_TEAM_TASKS_SUCCESS: {
      const { response = {} } = action;
      const { result = {} } = response;
      const { related = {} } = result;
      const { Project = {} } = related;

      return mergeEntities(state, fromJS(Project));
    }
    case actionTypes.FETCH_PROJECTS_FOR_SCREEN_SUCCESS:
    case actionTypes.FETCH_PROJECTS_DETAILS_SUCCESS:
      if (!action.response || !action.response.entities || !action.response.entities.projects) {
        return state;
      }
      return mergeWithPartialData(state, action.response.result, action.response.entities.projects);
    case actionTypes.ADD_MEMBERS_TO_PROJECT_SUCCESS: {
      const { projectId } = action;
      if (projectId) {
        state = state.set(`${projectId}`, project(state.get(`${projectId}`), action));
      }
      return state;
    }
    case actionTypes.REMOVE_MEMBER_FROM_PROJECT_SUCCESS: {
      const { projectId, userId } = action;
      if (projectId) {
        state = state.setIn([`${projectId}`, `members`], List(action.response));
        if (state.getIn([`${projectId}`, "leader_id"]) === userId) {
          state = state.setIn([`${projectId}`, `leader_id`], 0);
        }
        const subscribers = state.getIn([`${projectId}`, "subscribers"]);
        if (subscribers && subscribers.includes(userId)) {
          state = state.setIn(
            [`${projectId}`, "subscribers"],
            subscribers.filter((id) => id !== userId)
          );
        }
      }
      return state;
    }
    case actionTypes.REPLACE_MEMBER_ON_PROJECT_SUCCESS: {
      const { projectId, userId, replaceWithUserId, response } = action;
      if (projectId) {
        state = state.setIn([`${projectId}`, `members`], List(response));
        if (state.getIn([`${projectId}`, "leader_id"]) === userId) {
          state = state.setIn([`${projectId}`, `leader_id`], replaceWithUserId);
        }
      }
      return state;
    }

    case actionTypes.ADD_USER_TO_PROJECTS_SUCCESS: {
      const { projectIds = [], userId } = action;

      if (projectIds.length > 0 && userId) {
        projectIds.forEach((projectId) => {
          const projectState = state.get(`${projectId}`);
          if (projectState) {
            const members = projectState.get("members");
            if (members.includes(userId)) {
              return;
            }
            const updatedMembers = members.push(userId);
            const updatedProject = projectState.set("members", updatedMembers);
            state = state.set(`${projectId}`, updatedProject);
          }
        });
      }
      return state;
    }

    case actionTypes.STOPWATCHES_FETCH_SUCCESS: {
      const { response = {} } = action;
      const { entities = {} } = response;
      const { projects = [] } = entities;

      return mergeEntities(state, fromJS(projects));
    }
    case actionTypes.BATCH_EDIT_BUDGET_TYPES_SUCCESS: {
      const { response = {} } = action;
      const { entities = {} } = response;
      const { projects = [] } = entities;
      return mergeEntities(state, fromJS(projects));
    }
    case actionTypes.FETCH_USER_TASKS_SUCCESS: {
      const { response = {} } = action;
      const { entities = {} } = response;
      const { projects } = entities;

      if (projects) {
        return mergeEntities(state, fromJS(projects));
      }

      return state;
    }
    default:
      return state;
  }
};

export const projects_id_name = (state = List(), action) => {
  switch (action.type) {
    case actionTypes.LOAD_PROJECTS_ID_NAME:
      return fromJS(action.response).sort((str1, str2) => {
        return str1.localeCompare(str2);
      });
    default:
      return state;
  }
};

export default projects;
